import { scopedThreadKey } from "@afar-research/client-runtime/environment";
import type { PreviewAgentPresentation, ScopedThreadRef } from "@afar-research/contracts";

import { resolveBrowserDefaults } from "~/browser/browserDefaults";
import { browserMiniPlayerSource, usePreviewMiniPlayerStore } from "~/previewMiniPlayerStore";
import { useRightPanelStore } from "~/rightPanelStore";

/**
 * Whether an agent-opened tab should appear for this user: an explicit ask from
 * the agent wins, and otherwise the user's own auto-show preference decides.
 */
export function agentTabShows(
  presentation: PreviewAgentPresentation,
  autoShowFloatingPreview: boolean,
): boolean {
  return presentation === "show" || (presentation === "default" && autoShowFloatingPreview);
}

/** Tabs already presented, so a replayed `opened` event never reopens a panel the user closed. */
const presented = new Set<string>();

/**
 * Shows a tab an agent opened in the Environment's browser (ADR-0137) the way
 * a Desktop Client used to when it hosted the browser itself: in the right
 * panel and the floating player, so the page the agent is on is the one the
 * user reaches for.
 */
export async function presentAgentOpenedTab(
  threadRef: ScopedThreadRef,
  tabId: string,
  presentation: PreviewAgentPresentation,
): Promise<void> {
  const key = `${scopedThreadKey(threadRef)}\u0000${tabId}`;
  if (presented.has(key)) return;
  presented.add(key);
  const { autoShowFloatingPreview } = await resolveBrowserDefaults().catch(() => ({
    autoShowFloatingPreview: true,
  }));
  if (!agentTabShows(presentation, autoShowFloatingPreview)) return;
  useRightPanelStore.getState().openBrowser(threadRef, tabId);
  usePreviewMiniPlayerStore.getState().open(threadRef, browserMiniPlayerSource(tabId));
}
